/**
 * v3.19-1417 PWA-REFRESH — Deployment version watcher.
 *
 * PROBLEM: Once the Hub is installed as a PWA (Android / iOS home-screen)
 * OR loaded in a browser tab that isn't hard-refreshed after a deploy,
 * the service worker + HTTP cache can keep serving stale JavaScript for
 * hours or days.  This is exactly what happened with v3.19-1416 — the
 * production build shipped, but Android users kept seeing the old
 * ActiveCallPill (with the RECORD button that was removed in v3.19-1413).
 *
 * SOLUTION: On boot, this component snapshots the current version from
 * /version.json (cache-busted) and then polls the same URL every 60s.
 * When it observes a mismatch it surfaces a sticky Sonner toast that
 * tells the user a new release is available and offers a one-click
 * "Refresh now" button that:
 *   1.  Tells the active service worker to skipWaiting (so the newly
 *       cached SW takes over immediately),
 *   2.  Deletes every Cache Storage entry (fetch caches),
 *   3.  Reloads the page bypassing the HTTP cache.
 *
 * This is a permanent piece of infrastructure — every future deployment
 * will benefit from it.  The version.json file is the SSOT and MUST be
 * bumped on every release (already project standard).
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';

const VERSION_URL = '/version.json';
const POLL_MS     = 60 * 1000;   // 60s — cheap, static file
const TOAST_ID    = 'ptc-hub-version-refresh';

async function fetchVersion() {
  // Cache-buster ensures we always ask the CDN for the freshest copy.
  const url = `${VERSION_URL}?t=${Date.now()}`;
  const res = await fetch(url, { cache: 'no-store', credentials: 'same-origin' });
  if (!res.ok) throw new Error(`version.json HTTP ${res.status}`);
  const j = await res.json();
  // Support both "3.19.1417" (semver) and legacy tag layouts.
  return j?.version || `${j?.major}.${j?.minor}.${j?.patch}${j?.letter || ''}`;
}

async function hardRefresh() {
  try {
    // Nudge any waiting service worker to activate immediately.
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const r of regs) {
        try { r.waiting?.postMessage({ type: 'SKIP_WAITING' }); } catch (_) { /* noop */ }
        try { await r.update(); } catch (_) { /* noop */ }
      }
    }
    // Wipe all fetch caches so /static/**/*.js hashes are re-resolved.
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (e) {
    // Best-effort — proceed to reload regardless.
  }
  // Cache-bypassing reload.  location.reload(true) is deprecated but honored
  // by evergreen browsers; setting href with a query buster is the fallback.
  try {
    window.location.reload(true);
  } catch (e) {
    const u = new URL(window.location.href);
    u.searchParams.set('v', String(Date.now()));
    window.location.href = u.toString();
  }
}

export default function VersionRefreshWatcher() {
  const bootVersionRef = useRef(null);
  const promptedRef    = useRef(false);
  const timerRef       = useRef(null);

  useEffect(() => {
    let cancelled = false;

    // Capture the version we booted with.  If this fetch fails we simply
    // disable the watcher — do not annoy the user with fetch noise.
    (async () => {
      try {
        const v = await fetchVersion();
        if (cancelled) return;
        bootVersionRef.current = v;
      } catch (_) { /* ignore */ }
    })();

    const check = async () => {
      if (promptedRef.current || !bootVersionRef.current) return;
      try {
        const current = await fetchVersion();
        if (cancelled) return;
        if (current && current !== bootVersionRef.current) {
          promptedRef.current = true;
          toast(
            `Hub was updated to ${current}. Refresh to load the latest version.`,
            {
              id:       TOAST_ID,
              duration: Infinity,
              action: {
                label:   'Refresh now',
                onClick: () => { hardRefresh(); },
              },
              icon: <RefreshCw className="h-4 w-4" />,
              dismissible: false,
            }
          );
        }
      } catch (_) { /* transient network — try again next tick */ }
    };

    timerRef.current = window.setInterval(check, POLL_MS);

    // Also check when tab regains focus (common PWA pattern — user
    // brings phone back to Hub after a deploy).
    const onFocus = () => { check(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      cancelled = true;
      if (timerRef.current) window.clearInterval(timerRef.current);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  // Listen for a fresh SW taking control (fired after skipWaiting +
  // clients.claim in public/sw.js) — reload once for good measure.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    let reloaded = false;
    const onCtrlChange = () => {
      if (reloaded) return;
      reloaded = true;
      // A tiny delay lets the toast dismiss animation finish first.
      window.setTimeout(() => { window.location.reload(); }, 250);
    };
    navigator.serviceWorker.addEventListener('controllerchange', onCtrlChange);
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onCtrlChange);
    };
  }, []);

  return null;
}
